import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  alpha,
  useTheme,
  AppBar,
  Toolbar,
  Fab,
  Icon,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { usePOSService } from "../../hooks/usePOSService";
import { OrdersList } from "../../components/OrdersList/OrdersList";
import { SessionCheck } from "../../components/SessionCheck/SessionCheck";
import { OrderResponse } from "../../types/pos.types";
import { useAuth } from "../../../auth/hooks/useAuth";
import { toast } from "react-toastify";
import { useUserRole } from "../../hooks/useUserRole";

export const OrdersPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { posService: service, sessionId } = usePOSService();
  const { user } = useAuth();
  const userRole = useUserRole();
  const isProcessing = localStorage.getItem("paymentInProcess");
  const draftId = localStorage.getItem("DraftId");
  if (isProcessing) {
    localStorage.removeItem("paymentInProcess");
  }
  if (draftId) {
    localStorage.removeItem("DraftId");
  }

  // 🔥 Auto-redirect if user is waiter
  useEffect(() => {
    if (userRole === "waiter") {
      navigate("/pos/waiterDashboard", { replace: true });
    }
  }, [userRole, navigate]);

  const [orders, setOrders] = React.useState<OrderResponse[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [refresh, setRefresh] = React.useState(0);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await service.getOrders({
        session_id: sessionId || "",
        pageIndex: 1,
        pageSize: 50,
        sortOrder: "desc",
      });
      if (response.success) {
        setOrders(response.data.items);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchOrders();
    }
  }, [sessionId, refresh]);

  const handleVoidOrder = async (orderId: string, reason: string) => {

    try {
      const response = await service.voidOrder({
        id: orderId,
        reason: reason,
      });
      if (response.success) {
        setRefresh((prev) => prev + 1);
        toast.success("Order voided successfully");
      } else {
        toast.error(response.message || "Failed to void order");
      }
    } catch (error) {
      console.error("Error voiding order:", error);
      toast.error("Failed to void order");
    }
  };

  const handleContinueOrder = async (order: OrderResponse) => {

    try {
      const response = await service.getOrder(order.id);
      if (response.success) {
        navigate(`/pos/checkout/${order.id}`);
      }
    } catch (error) {
      console.error("Error continuing order:", error);
    }
  };

  const handleUpdateOrder = async (order: OrderResponse) => {

    try {
      const response = await service.getOrder(order.order_id);

    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const handleResumeOrder = async (order: OrderResponse) => {

    try {
      const response = await service.getOrder(order.order_id);
      if (response) {
        navigate(`/pos/menu?orderId=${order.order_id}`);
        localStorage.setItem("DraftId", order.order_id);
      } else {
        toast.error("Failed to load draft");
      }
    } catch (error) {
      console.error("Error resuming order:", error);
      toast.error("Failed to resume draft");
    }
  };

  const handlePayOrder = async (order: OrderResponse) => {

    try {
      // Load full order data if needed
      const response = await service.getOrder(order.order_id);
      if (response.success && response.data) {
        // Save to localStorage
        localStorage.setItem("paymentInProcess", order.order_id);

        // Navigate to checkout
        navigate(`/pos/menu?orderId=${order.order_id}`);
      } else {
        toast.error("Failed to load order");
      }
    } catch (error) {
      console.error("Error loading order:", error);
      toast.error("An error occurred while loading the order");
    }
  };

  return (
    <SessionCheck>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
        }}
      >
        {/* Custom AppBar */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: "#1E8078",
            borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
          }}
        >
          <Toolbar sx={{ gap: 2 }}>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
              Orders
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box sx={{ flex: 1, p: 3, overflow: "hidden", position: "relative" }}>
          <Box
            sx={{
              display: "flex",
              gap: 3,
              height: "100%",
              maxWidth: 1920,
              mx: "auto",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                borderRadius: 4,
                overflow: "hidden",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: "blur(8px)",
                boxShadow: `0 4px 20px ${alpha(
                  theme.palette.common.black,
                  0.05
                )}`,
              }}
            >
              <OrdersList
                orders={orders}
                loading={loading}
                onVoidOrder={handleVoidOrder}
                onContinueOrder={handleContinueOrder}
                onUpdateOrder={handleUpdateOrder}
                onPayOrder={handlePayOrder}
                onResumeOrder={handleResumeOrder}
                is_pos_supervisor={user?.is_pos_supervisor || false}
                refresh={refresh}            // 👈 Pass refresh value
                setRefresh={setRefresh}
              />
            </Paper>
          </Box>
          <Fab
            variant="extended"
            color="primary"
            sx={{
              position: "fixed",
              bottom: 32,
              right: 32,
              backgroundColor: "#1E8078",
              px: 3,
              py: 2,
              "&:hover": {
                backgroundColor: "#166F68",
              },
              boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
              transition: "all 0.3s ease",
              "&:active": {
                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              },
            }}
            onClick={() => navigate("/pos/menu")}
          >
            <Icon sx={{ mr: 1 }}>add</Icon>
            Create Order
          </Fab>
        </Box>

      </Box>
    </SessionCheck>
  );
};
